/**
 * This file provides the necessary wrappers and utility functions to provide
 * analytics data for Quick Find result positions. Quick Find analytics contains
 * data such as the number of results, the number of sections, and the
 * position / section of a selected result.
 *
 * It does this by using the DOM as the source of truth rather than having to
 * maintain section objects and indexes in the code. This allows rendering
 * display components to be simplified and could prove to be more robust, e.g.
 * if a search result doesn't render due to an error and subsequently hits
 * its error boundary, section and results count remain correct.
 *
 * There is however the requirement that all heading and results components
 * are siblings in the DOM tree.
 */

/* eslint-disable @atlaskit/platform/no-direct-document-usage */
/* eslint-disable @atlaskit/ui-styling-standard/no-classname-prop */

import React from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import type { ConnectorSource } from '../../constants/schemas/3p-config';
import type { ResultShown } from '../events/types';

const { differenceInMinutes, parseISO, isValid } = require('date-fns');

export const SEARCH_DIALOG_HEADING_CLASS = 'search-dialog-heading';
export const SEARCH_DIALOG_RESULT_CLASS = 'search-dialog-result';
export const BULK_CONNECT_BANNER_CLASS = 'bulk-connect-banner';

type SearchDialogHeaderAnalyticsWrapperProps = {
	id: string;
};

const resultTypes = [
	'peopleAndTeams',
	'recentQueries',
	'recentActivities',
	'queryFilters',
	'searchResults',
	'zeroQueries',
	'autocompleteSuggestions',
	'filterSuggestions',
	'intentRouter',
	'recommendations',
	'intentRouterWorkItem',
] as const;
export type ResultType = (typeof resultTypes)[number];

type SearchDialogResultAnalyticsWrapperProps = {
	resultType: ResultType;
	product?: string;
	connectorType?: ConnectorSource;
	minutesSinceActivityTimestamp?: number | null;
} & React.HTMLAttributes<HTMLDivElement>;

export const SearchDialogHeaderAnalyticsWrapper: React.ForwardRefExoticComponent<
	SearchDialogHeaderAnalyticsWrapperProps & {
		children?: React.ReactNode | undefined;
	} & React.RefAttributes<HTMLDivElement>
> = React.forwardRef<
	HTMLDivElement,
	React.PropsWithChildren<SearchDialogHeaderAnalyticsWrapperProps>
>(({ children, id, ...rest }, ref) => {
	return (
		// Ignoring this check as class is not being used for styling
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
		<div
			className={SEARCH_DIALOG_HEADING_CLASS}
			data-section-id={id}
			data-testid="search-dialog-heading-analytics-wrapper"
			ref={ref}
			{...rest}
		>
			{children}
		</div>
	);
});

export const SearchDialogResultAnalyticsWrapper: React.ForwardRefExoticComponent<
	{
		resultType: ResultType;
		product?: string;
		connectorType?: ConnectorSource;
		minutesSinceActivityTimestamp?: number | null;
	} & React.HTMLAttributes<HTMLDivElement> & {
			children?: React.ReactNode | undefined;
		} & React.RefAttributes<HTMLDivElement>
> = React.forwardRef<
	HTMLDivElement,
	React.PropsWithChildren<SearchDialogResultAnalyticsWrapperProps>
>(
	(
		{ children, resultType, product, id, connectorType, minutesSinceActivityTimestamp, ...rest },
		ref,
	) => {
		return (
			<div
				// Ignoring this check as class is not being used for styling
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
				className={SEARCH_DIALOG_RESULT_CLASS}
				data-result-type={resultType}
				data-product={product}
				data-id={id}
				data-connector-type={connectorType}
				data-since-activity-timestamp={minutesSinceActivityTimestamp}
				ref={ref}
				{...rest}
			>
				{children}
			</div>
		);
	},
);

export const BulkConnectBannerAnalyticsWrapper: React.ForwardRefExoticComponent<
	React.HTMLAttributes<HTMLDivElement> & {
		children?: React.ReactNode | undefined;
	} & React.RefAttributes<HTMLDivElement>
> = React.forwardRef<HTMLDivElement, React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>>(
	({ children, ...rest }, ref) => {
		return (
			// Ignoring this check as class is not being used for styling
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
			<div className={BULK_CONNECT_BANNER_CLASS} ref={ref} {...rest}>
				{children}
			</div>
		);
	},
);

export const getProductsInResults: () => string[] = (): string[] => {
	const resultNodes = Array.from(document.getElementsByClassName(SEARCH_DIALOG_RESULT_CLASS));

	const productSet = new Set<string>();
	for (let i = 0; i < resultNodes.length; i++) {
		const product = resultNodes[i].getAttribute('data-product');
		product ? productSet.add(product) : null;
	}

	const productsInResults = Array.from(productSet);
	return productsInResults;
};

export const getIsBulkConnectBannerShown: () => boolean = (): boolean => {
	const bannerNode = document.querySelector(`.${BULK_CONNECT_BANNER_CLASS}`);
	return bannerNode !== null;
};

const isNotRecentQuery = (product: string | null): product is string => {
	return Boolean(product);
};

export function calculateMinuteDifference(compareWithTimeInMs?: unknown): number | null {
	try {
		if (typeof compareWithTimeInMs !== 'string') {
			return null;
		}

		const startTime = parseISO(compareWithTimeInMs);

		if (!isValid(startTime)) {
			return null;
		}

		const difference = differenceInMinutes(new Date(), startTime, 'floor');
		return difference >= 0 ? difference : null;
	} catch {
		// Swallowing error, bad timestamp should not throw error
		return null;
	}
}

/**
 * Converts a camelCase resultType to a kebab-case sectionId
 * (e.g. 'recentActivities' -> 'recent-activities') for use in
 * resultsShown analytics.
 */
export const resultTypeToSectionId = (resultType: string): string => {
	return resultType.replace(/([A-Z])/g, '-$1').toLowerCase();
};

export const getResultsShown: () => ResultShown[] = (): ResultShown[] => {
	const resultNodes = Array.from(document.getElementsByClassName(SEARCH_DIALOG_RESULT_CLASS));

	const resultList: ResultShown[] = [];
	for (let i = 0; i < resultNodes.length; i++) {
		const id = resultNodes[i].getAttribute('data-id');
		const product = resultNodes[i].getAttribute('data-product');
		const connectorType = resultNodes[i].getAttribute('data-connector-type');
		const resultType = resultNodes[i].getAttribute('data-result-type');
		const rawMinutesSinceActivityTimestamp = parseInt(
			resultNodes[i].getAttribute('data-since-activity-timestamp') || '',
			10,
		);
		const minutesSinceActivityTimestamp = isNaN(rawMinutesSinceActivityTimestamp)
			? null
			: rawMinutesSinceActivityTimestamp;

		if (id && isNotRecentQuery(product)) {
			const resultShown: ResultShown = {
				id,
				product,
				position: i,
				metadata: {
					minutesSinceActivityTimestamp,
					...(connectorType && { connectorType: connectorType as ConnectorSource }),
					...(fg('rovo_search_add_section_id_to_results_shown') && {
						sectionId: resultType ? resultTypeToSectionId(resultType) : null,
					}),
				},
			};
			resultList.push(resultShown);
		}
	}

	return resultList;
};

export const getResultTypeCounts: () => Record<ResultType, number> = () => {
	const resultNodes = Array.from(document.getElementsByClassName(SEARCH_DIALOG_RESULT_CLASS));

	const typesToCount = fg('qf_add_result_type_in_analytics')
		? resultTypes
		: resultTypes.filter((type) => type !== 'recommendations');

	return typesToCount.reduce(
		(o, resultType) => {
			o[resultType] =
				resultNodes.filter((node) => node.getAttribute('data-result-type') === resultType).length ||
				0;
			return o;
		},
		{} as Record<ResultType, number>,
	);
};

const getAllHeadingsAndResults = () => {
	const headingNodes = Array.from(document.getElementsByClassName(SEARCH_DIALOG_HEADING_CLASS));
	const resultNodes = Array.from(document.getElementsByClassName(SEARCH_DIALOG_RESULT_CLASS));

	if (!headingNodes.length || !resultNodes.length) {
		return {
			allNodes: [],
			headingNodes: [],
			resultNodes: [],
		};
	}

	const parentNode = headingNodes.length
		? headingNodes[0].parentNode
		: resultNodes.length
			? resultNodes[0].parentNode
			: undefined;

	if (!parentNode) {
		throw new Error('Could not find a parent node for headings and results');
	}

	const allNodes = Array.from(parentNode.children).filter((e) => isHeading(e) || isResult(e));

	return {
		allNodes,
		headingNodes,
		resultNodes,
	};
};

const isHeading = (e: Element): e is HTMLElement => {
	return e.classList.contains(SEARCH_DIALOG_HEADING_CLASS);
};

const isResult = (e: Element): e is HTMLElement => {
	return (
		e.classList.contains(SEARCH_DIALOG_RESULT_CLASS) ||
		e.querySelector(`.${SEARCH_DIALOG_RESULT_CLASS}`) !== null
	);
};

export const getSectionsAndResultsCount: () =>
	| {
			sectionCount: number;
			resultCount: number;
	  }
	| {
			sectionCount?: undefined;
			resultCount?: undefined;
	  } = () => {
	try {
		const { allNodes, headingNodes, resultNodes } = getAllHeadingsAndResults();

		// If the first node is a result, then we count it as a section
		const sectionCount = allNodes.length
			? headingNodes.length + (isResult(allNodes[0]) ? 1 : 0)
			: 0;

		return {
			sectionCount,
			resultCount: resultNodes.length,
		};
	} catch {
		return {};
	}
};
